import React, { useCallback, useEffect, useState } from "react";
import PrimaryButton from "../../../components/generic/buttons/PrimaryButton";
import { Card, Space, Spin, Tooltip } from "antd";
import { ListItem, ListItemAvatar, Typography } from "@mui/material";
import NoDataMessageComponent from "../../../components/generic/nodatamessage/NoDataMessageComponent";
import apiService from "../../../service/apiservice/apiService";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import CustomModal from "../../../components/generic/custommodal/CustomModal";
import SecondaryButton from "../../../components/generic/buttons/SecondaryButton";
import SmartIcon from "../../../components/generic/genericIcon/SmartIcon";
import AddEditOfficeForm from "./AddEditOfficeForm";

const OfficeSettings = () => {
  const organizationIdentifier = useGetOrgIdentifier();
  const [offices, setOffices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isModalVisible, setModalVisible] = useState(false);
  const [officeDetails, setOfficeDetails] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [formAction, setFormAction] = useState("");

  const trimIfString = (value) =>
    typeof value === "string" ? value.trim() : value;

  const handleClick = async (e) => {
    e.preventDefault();
    setErrors(validate(officeDetails));
    setIsSubmit(true);
  };
  const validate = (values) => {
    const errors = {};
    if (!trimIfString(values?.name)) {
      errors.name = "Name is required!";
    }
    if (!Array.isArray(values?.teams) || values?.teams.length === 0) {
      errors.teams = "Team is required";
    }
    return errors;
  };

  const addClick = () => {
    setOfficeDetails(null);
    setFormAction("add");
    setModalVisible(true);
  };

  const editClick = async (id) => {
    const response = await apiService.get(
      `niramayafoundation/niramayaoffice/api/v1/offices/${id}`,
    );
    setOfficeDetails(response);
    setFormAction("edit");
    setModalVisible(true);
  };

  const getOffices = useCallback(async () => {
    setIsLoading(true);
    const endpoint = "niramayafoundation/niramayaoffice/api/v1/offices";
    const params = {
      organizationIdentifier,
    };
    const response = await apiService.get(endpoint, params);
    setOffices(response);
    setIsLoading(false);
  }, [organizationIdentifier]);

  const handleSubmit = useCallback(async () => {
    setSubmitLoading(true);
    const payload = {
      organizationIdentifier,
      name: trimIfString(officeDetails?.name),
      address: trimIfString(
        officeDetails?.completeAddress || officeDetails?.address,
      ),
      streetAddress: trimIfString(officeDetails?.streetAddress),
      addressLocality: trimIfString(officeDetails?.addressLocality),
      addressRegion: trimIfString(officeDetails?.addressRegion),
      postalCode: trimIfString(officeDetails?.postalCode),
      addressCountry: trimIfString(officeDetails?.addressCountry),
      notes: trimIfString(officeDetails?.notes),
      teams: officeDetails?.teams,
    };
    const url =
      formAction === "edit"
        ? `niramayafoundation/niramayaoffice/api/v1/offices/${officeDetails?.id}`
        : "niramayafoundation/niramayaoffice/api/v1/offices";
    const method = formAction === "edit" ? "put" : "post";
    const successMessage =
      formAction === "edit"
        ? `Office updated successfully`
        : `Added Office successfully`;
    const errorMessage =
      formAction === "edit"
        ? `Error while updating Office`
        : `Error while adding Office`;

    const response = await apiService[method](
      url,
      payload,
      successMessage,
      errorMessage,
    );

    if (response?.id) {
      setIsSubmit(false);
      setModalVisible(false);
      getOffices();
      setOfficeDetails(null);
    }
    setSubmitLoading(false);
  }, [
    formAction,
    getOffices,
    officeDetails?.address,
    officeDetails?.addressCountry,
    officeDetails?.addressLocality,
    officeDetails?.addressRegion,
    officeDetails?.completeAddress,
    officeDetails?.id,
    officeDetails?.name,
    officeDetails?.notes,
    officeDetails?.postalCode,
    officeDetails?.streetAddress,
    officeDetails?.teams,
    organizationIdentifier,
  ]);

  useEffect(() => {
    if (Object.keys(errors).length === 0 && isSubmit) {
      handleSubmit();
    }
  }, [errors, handleSubmit, isSubmit]);

  useEffect(() => {
    getOffices();
  }, [getOffices]);

  const customModelButtons = (
    <>
      <SecondaryButton
        buttonText={"Cancel"}
        handleOnClick={() => setModalVisible(false)}
      />
      <PrimaryButton
        buttonText={formAction === "edit" ? "Edit" : "Add"}
        handleOnClick={handleClick}
        loading={submitLoading}
      />
    </>
  );

  return (
    <>
      <Card
        className="services-card"
        title={
          <>
            <div className="preference-card-title">
              <strong className="services-card-title-font">Offices</strong>
              <PrimaryButton
                buttonText={`Create New Office`}
                handleOnClick={addClick}
              />
            </div>
          </>
        }
      >
        {isLoading ? (
          <Spin className="spinnerCenter" />
        ) : offices && offices?.length ? (
          offices?.map((office) => (
            <ListItem className="services-listitem" key={office?.id}>
              <Typography className="services-listitem-font">
                {office?.name}
              </Typography>
              <ListItemAvatar className="services-list-item-action">
                <Space size="middle">
                  <Tooltip title="Edit" placement="bottom">
                    <>
                      <SmartIcon
                        iconKey={"edit"}
                        width="20"
                        height="20"
                        handleIconClick={() => editClick(office?.id)}
                      />
                    </>
                  </Tooltip>
                </Space>
              </ListItemAvatar>
            </ListItem>
          ))
        ) : (
          <NoDataMessageComponent text="No data available" />
        )}
      </Card>

      {isModalVisible && (
        <CustomModal
          closable={true}
          isModalOpen={isModalVisible}
          footer={[customModelButtons]}
          closeModal={setModalVisible}
          title={
            formAction === "edit" ? `Edit Office Details` : `Create New Office`
          }
        >
          <AddEditOfficeForm
            officeDetails={officeDetails}
            setOfficeDetails={setOfficeDetails}
            errors={errors}
          />
        </CustomModal>
      )}
    </>
  );
};

export default OfficeSettings;
